import { getResourceInfo } from '@/api';
import { WS_EVENTS, WebSocketContext } from '@/providers/WebSocketProvider';
import { ResourceStatus } from '@/stores/resource';
import { getUrlExpiry } from '@/util/url';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { SetterOrUpdater } from 'recoil';

export interface ResourceInfo {
  originalUrl: string;
  transcodedUrl: string;
  duration: number;
  // Absolute expiry timestamp (ms since epoch), or null when the URL carries
  // no recognisable expiry parameter (ISSUE-027).
  originalExpires: number | null;
}
// AddFilesReturn — uploadFile passes addFiles' return value through to the
// caller (ISSUE-028): useResultUpload resolves with the created resource ids
// so AudioEditorPanel can record the id that owns the result-list entry.
// Call sites whose addFiles returns void are unaffected.
const useUploadEvents = <AddFilesReturn = void>(
  uploadingStatus: ResourceStatus,
  setUploadingStatus: SetterOrUpdater<ResourceStatus>,
  complete: (data: ResourceInfo, resourceId: string) => void,
  addFiles: (sessionId: string, files: File[], group?: string) => AddFilesReturn
) => {
  const { sessionId, on, off } = useContext(WebSocketContext);
  const uploadingStatusRef = useRef(uploadingStatus);
  uploadingStatusRef.current = uploadingStatus;
  const completeRef = useRef(complete);
  completeRef.current = complete;

  useEffect(() => {
    const onPending = ({ resource_id }: any) => {
      if (!uploadingStatusRef.current[resource_id]) return;
      setUploadingStatus((prev) => {
        return {
          ...prev,
          [resource_id]: 'PENDING',
        };
      });
    };
    const onProcessing = ({ resource_id }: any) => {
      if (!uploadingStatusRef.current[resource_id]) return;
      setUploadingStatus((prev) => {
        return {
          ...prev,
          [resource_id]: 'PROCESSING',
        };
      });
    };
    // ISSUE-039: the handler stays async (the caller may await it) but never
    // rejects — WebSocketProvider's dispatch discards the returned promise, so
    // a rejection here would surface only as an unhandled rejection.
    const onComplete = async ({ resource_id }: any) => {
      if (!uploadingStatusRef.current[resource_id]) return;
      try {
        // Destructured inside the try so a malformed 200 (no `transcoded`
        // entry) shares the failure path with a rejected request.
        const {
          data: { data },
        } = await getResourceInfo(resource_id);
        completeRef.current(
          {
            originalUrl: data.original.url,
            transcodedUrl: data.transcoded[0].url,
            duration: data.original.metadata.props.duration,
            originalExpires: getUrlExpiry(data.original.url),
          },
          resource_id
        );
        // COMPLETE is written AFTER the metadata lands, so the status can
        // never mean "done" while the item has no url and no duration.
        setUploadingStatus((prev) => {
          return {
            ...prev,
            [resource_id]: 'COMPLETE',
          };
        });
      } catch (error) {
        // console.error survives production builds (see ErrorBoundary.tsx).
        console.error('failed to load resource info', resource_id, error);
        setUploadingStatus((prev) => {
          return {
            ...prev,
            [resource_id]: 'FAILED',
          };
        });
      }
    };
    const onFailed = ({ resource_id }: any) => {
      if (!uploadingStatusRef.current[resource_id]) return;
      setUploadingStatus((prev) => {
        return {
          ...prev,
          [resource_id]: 'FAILED',
        };
      });
    };
    on(WS_EVENTS.JOB_PENDING, onPending);
    on(WS_EVENTS.JOB_PROCESSING, onProcessing);
    on(WS_EVENTS.JOB_DONE, onComplete);
    on(WS_EVENTS.JOB_FAILED, onFailed);
    return () => {
      off(WS_EVENTS.JOB_PENDING, onPending);
      off(WS_EVENTS.JOB_PROCESSING, onProcessing);
      off(WS_EVENTS.JOB_DONE, onComplete);
      off(WS_EVENTS.JOB_FAILED, onFailed);
    };
  }, [on, off, setUploadingStatus]);
  const uploadFile = useCallback(
    (data: File[], group?: string) =>
      addFiles(sessionId as string, data, group),
    [sessionId, addFiles]
  );

  return { uploadFile };
};
export default useUploadEvents;
